import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Clapperboard, ImageOff, RefreshCw, VideoOff } from 'lucide-react';
import { videoFirstFrameSource } from '../mediaPreview';
import { getPublishedVideoCase } from './videoCaseApi';
import { MediaPreviewButton } from '../components/MediaPreview';

function isHttpUrl(value) {
  const source = String(value || '').trim();
  if (!/^https?:\/\//i.test(source)) return false;
  try {
    const url = new URL(source);
    return Boolean(url.hostname && (url.protocol === 'http:' || url.protocol === 'https:'));
  } catch {
    return false;
  }
}

function isSafeImageUrl(value) {
  const source = String(value || '').trim();
  if (isHttpUrl(source)) return true;
  if (!/^\/(?!\/)/.test(source) || /[\s\\]/.test(source)) return false;
  try {
    const pathname = decodeURIComponent(source.split(/[?#]/, 1)[0]);
    if (pathname === '/' || /[\s\\]/.test(pathname)) return false;
    return !pathname.split('/').some(segment => segment === '.' || segment === '..');
  } catch {
    return false;
  }
}

export default function VideoCaseDetailPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [mediaError, setMediaError] = useState(false);
  const videoRef = useRef(null);

  const loadCase = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setMediaError(false);
    try {
      setItem(await getPublishedVideoCase(caseId));
    } catch (error) {
      setItem(null);
      setLoadError(getErrorMessage(error, '案例加载失败'));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadCase(); }, [loadCase]);

  const canViewProcess = Boolean(item?.can_view_process && item?.canvas_nodes_id);
  const imageUrl = isSafeImageUrl(item?.image_url) ? String(item.image_url).trim() : '';
  const playableUrl = isHttpUrl(item?.video_url) ? String(item.video_url).trim() : '';
  const mediaType = imageUrl ? '图片' : playableUrl ? '视频' : '内容';
  const relatedCases = Array.isArray(item?.related_cases) ? item.related_cases : [];

  return (
    <div className="min-h-full bg-[var(--vnext-page)] text-[var(--vnext-ink)]">
      <div className="mx-auto w-full max-w-[1560px] px-3 pb-16 pt-14 sm:px-5 sm:pt-5 lg:px-8">
        <button
          type="button"
          onClick={() => navigate('/learn')}
          className="mb-3 inline-flex h-9 items-center gap-2 rounded-[9px] border border-[var(--vnext-line)] bg-[var(--vnext-surface)] px-3 text-[11px] font-medium text-[var(--vnext-muted)] transition hover:bg-[var(--vnext-surface-strong)] hover:text-[var(--vnext-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vnext-lavender)]"
        >
          <ArrowLeft size={14} />
          返回学习案例
        </button>

        {loading ? (
          <div className="grid min-h-[72vh] place-items-center text-[11px] text-[var(--vnext-muted)]">正在打开案例…</div>
        ) : loadError ? (
          <section className="grid min-h-[72vh] place-items-center text-center" role="alert">
            <div>
              <Clapperboard size={28} className="mx-auto text-[var(--vnext-muted)]" />
              <h1 className="mt-4 text-[20px] font-semibold tracking-[-0.02em] text-[var(--vnext-ink)]">暂时无法打开</h1>
              <p className="mt-2 text-[11px] text-[var(--vnext-muted)]">{loadError}</p>
              <button type="button" onClick={loadCase} className="mt-5 inline-flex h-9 items-center gap-2 rounded-[9px] bg-[#352f3b] px-3.5 text-[11px] font-medium text-white hover:bg-[#443d4a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vnext-lavender)] dark:bg-white dark:text-black dark:hover:bg-white/88">
                <RefreshCw size={13} />重新加载
              </button>
            </div>
          </section>
        ) : (
          <main>
            <section className="relative overflow-hidden rounded-[14px] bg-black ring-1 ring-black/[0.1] dark:ring-white/[0.08]" aria-label={`${item.title}案例${mediaType}`}>
              <MediaPreviewButton src={imageUrl || playableUrl} type={imageUrl ? 'image' : 'video'} title={item.title} poster={item.cover}
                className="media-preview-trigger--overlay" onOpen={() => { if (videoRef.current && !videoRef.current.paused) videoRef.current.pause(); }} />
              {imageUrl ? (
                <div className="relative grid min-h-[42vh] place-items-center bg-black lg:min-h-[620px]">
                  <img
                    src={imageUrl}
                    alt={`${item.title}案例图片`}
                    className="max-h-[calc(100vh-116px)] w-full object-contain"
                    onError={() => setMediaError(true)}
                  />
                  {mediaError && (
                    <div className="absolute inset-0 grid place-items-center bg-black/86 text-center" role="alert">
                      <div>
                        <ImageOff size={26} className="mx-auto text-white/45" />
                        <p className="mt-3 text-[12px] text-white/72">图片加载失败，请稍后重试或联系运营检查地址。</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : playableUrl ? (
                <div className="relative min-h-[42vh] bg-black lg:h-[calc(100vh-116px)] lg:min-h-[620px]">
                  <video
                    ref={videoRef}
                    aria-label={`${item.title}案例视频播放器`}
                    className="h-full w-full object-contain"
                    controls
                    playsInline
                    preload="metadata"
                    poster={item.cover || undefined}
                    src={playableUrl}
                    onError={() => setMediaError(true)}
                  >
                    当前浏览器不支持视频播放。
                  </video>
                  {mediaError && (
                    <div className="absolute inset-0 grid place-items-center bg-black/86 text-center" role="alert">
                      <div>
                        <VideoOff size={26} className="mx-auto text-white/45" />
                        <p className="mt-3 text-[12px] text-white/72">视频加载失败，请稍后重试或联系运营检查地址。</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid aspect-video place-items-center text-center text-white/58" role="status">
                  <div><VideoOff size={28} className="mx-auto" /><p className="mt-3 text-[12px]">该案例暂无可展示的图片或视频</p></div>
                </div>
              )}
            </section>

            <section className="mx-auto max-w-[1180px] border-b border-[var(--vnext-line)] py-7 sm:py-9">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-[10px] text-[var(--vnext-muted)]">
                    <span className="rounded-[7px] border border-[var(--vnext-line)] bg-[var(--vnext-surface)] px-2 py-1">{item.category_name || item.category || '案例'}</span>
                    {item.tag && <span>{item.tag}</span>}
                    {item.creation_tags && <span>· {item.creation_tags}</span>}
                  </div>
                  <h1 className="mt-3 text-[24px] font-semibold tracking-[-0.035em] text-[var(--vnext-ink)] sm:text-[30px]">{item.title}</h1>
                  <p className="mt-3 max-w-[780px] text-[12px] leading-6 text-[var(--vnext-muted)]">{item.sub_title}</p>
                </div>

                <button type="button" onClick={() => navigate(`/canvas?create=1&case=${encodeURIComponent(item.id)}`)} className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-[10px] border border-[var(--vnext-line)] px-4 text-[11px] font-medium text-[var(--vnext-ink)] hover:bg-[var(--vnext-surface-strong)] focus-visible:ring-2 focus-visible:ring-[var(--vnext-lavender)]">用此案例创建</button>
                {canViewProcess && (
                  <button
                    type="button"
                    onClick={() => navigate(`/canvas/${encodeURIComponent(item.canvas_nodes_id)}`)}
                    className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-[10px] bg-[#352f3b] px-4 text-[11px] font-medium text-white transition hover:bg-[#443d4a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vnext-lavender)] dark:bg-white dark:text-black dark:hover:bg-white/88"
                  >
                    <Clapperboard size={14} />
                    查看制作过程
                  </button>
                )}
              </div>
            </section>

            <section className="mx-auto max-w-[1180px] pt-9" aria-label="可能感兴趣的案例">
              <div className="flex items-end justify-between gap-4">
                <div><p className="text-[9px] font-semibold tracking-[0.16em] text-[var(--vnext-muted)]">KEEP EXPLORING</p><h2 className="mt-1 text-[20px] font-semibold tracking-[-0.025em] text-[var(--vnext-ink)]">可能感兴趣</h2></div>
                <button type="button" onClick={() => navigate('/learn')} className="inline-flex items-center gap-1.5 text-[10px] text-[var(--vnext-muted)] transition hover:text-[var(--vnext-ink)]">查看全部 <ArrowUpRight size={12} /></button>
              </div>
              {relatedCases.length ? (
                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {relatedCases.map(related => (
                    <button type="button" key={related.id} onClick={() => navigate(`/learn/${encodeURIComponent(related.id)}`)} className="group overflow-hidden rounded-[13px] border border-[var(--vnext-line)] bg-[var(--vnext-surface)] text-left transition hover:-translate-y-0.5 hover:bg-[var(--vnext-surface-strong)]">
                      <div className="aspect-video overflow-hidden bg-black/[0.035] dark:bg-white/[0.03]">{related.cover || related.image_url ? <img src={related.cover || related.image_url} alt={`${related.title}封面`} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.035]" /> : related.video_url ? <video src={videoFirstFrameSource(related.video_url)} muted playsInline preload="metadata" aria-hidden="true" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.035]" /> : <VideoOff className="m-auto h-full text-stone-300 dark:text-white/15" size={25} />}</div>
                      <div className="p-3"><span className="text-[9px] text-[var(--vnext-muted)]">{related.category_name || related.category || '案例'}</span><h3 className="mt-1 line-clamp-1 text-[12px] font-medium text-[var(--vnext-ink)]">{related.title}</h3><p className="mt-1 line-clamp-1 text-[10px] text-[var(--vnext-muted)]">{related.sub_title || '继续探索创作方法'}</p></div>
                    </button>
                  ))}
                </div>
              ) : <div className="mt-5 rounded-[13px] border border-dashed border-[var(--vnext-line)] py-12 text-center text-[11px] text-[var(--vnext-muted)]">暂无更多推荐案例</div>}
            </section>
          </main>
        )}
      </div>
    </div>
  );
}
